<template>
    <div class="sales-horizon-chart">
        <div class="row g-2 mb-3">
            <div v-for="item in summary" :key="item.label" class="col">
                <div class="sales-horizon-chart__stat rounded-2 h-100">
                    <div class="small text-muted">{{ item.label }}</div>
                    <div class="fw-semibold">{{ item.value }}</div>
                    <div class="small text-muted">{{ item.note }}</div>
                </div>
            </div>
        </div>
        <div class="sales-horizon-chart__canvas">
            <canvas ref="canvas"></canvas>
        </div>
        <div class="sales-horizon-chart__legend d-flex flex-wrap justify-content-center mt-2">
            <span class="text-nowrap"><span class="sales-horizon-chart__symbol sales-horizon-chart__symbol--column"></span>Cena podle doby {{ chart.offerType === 'lease' ? 'pronájmu' : 'prodeje' }}</span>
            <span class="text-nowrap"><span class="sales-horizon-chart__symbol sales-horizon-chart__symbol--estimate"></span>Odhadní cena</span>
        </div>
    </div>
</template>
<script lang="ts">
import {
    differenceFromEstimate,
    formatAxisPrice,
    formatDays,
    formatMoney,
    formatPerMeter,
    formatProbability,
    formatSales,
    priceBounds,
    SALES_HORIZON_COLORS,
    SalesHorizonChartData,
    summaryItems,
} from '@/components/charts/salesHorizon';
import {Chart, Plugin} from 'chart.js';
import {computed, defineComponent, onBeforeUnmount, onMounted, PropType, ref} from 'vue';

/**
 * The chart of the prices by the time of sale of a real estate estimate: a column of the price of every sale mode with
 * its price and price per metre, the estimated price as a dashed line across them, a summary above and a legend
 * under them; the picture of the documents (GdSalesHorizonChartRenderer) draws the same numbers. The font is the one
 * of the page.
 */
export default defineComponent({
    props: {
        chart: {
            type: Object as PropType<SalesHorizonChartData>,
            required: true,
        },
    },
    setup(props) {
        const canvas = ref<HTMLCanvasElement | null>(null);
        const summary = computed(() => summaryItems(props.chart));
        let instance: Chart<'bar', number[], string[]> | null = null;

        onMounted(() => {
            const element = canvas.value as HTMLCanvasElement;
            const font = getComputedStyle(element).fontFamily;
            const data = props.chart;
            const bounds = priceBounds(data);

            // The price of a column and its price per metre above it, on white over the line of the estimate as the
            // picture of the documents (GdSalesHorizonChartRenderer)
            const columnLabels: Plugin<'bar'> = {
                id: 'salesHorizonColumnLabels',
                afterDatasetsDraw(chart) {
                    const context = chart.ctx;
                    const label = (text: string, x: number, y: number, style: string, color: string): void => {
                        context.font = style;
                        const width = context.measureText(text).width;
                        context.fillStyle = '#ffffff';
                        context.fillRect(x - width / 2 - 2, y - 11, width + 4, 14);
                        context.fillStyle = color;
                        context.fillText(text, x, y);
                    };
                    chart.getDatasetMeta(0).data.forEach((bar, index) => {
                        const saleMode = data.saleModes[index];
                        context.save();
                        context.textAlign = 'center';
                        label(formatMoney(saleMode.price), bar.x, bar.y - 20, `600 12px ${font}`, SALES_HORIZON_COLORS.ink);
                        label(formatPerMeter(saleMode.pricePerMeter), bar.x, bar.y - 6, `11px ${font}`, SALES_HORIZON_COLORS.muted);
                        context.restore();
                    });
                },
            };

            // The estimated price across the columns
            const estimateLine: Plugin<'bar'> = {
                id: 'salesHorizonEstimate',
                afterDatasetsDraw(chart) {
                    const y = chart.scales.y.getPixelForValue(data.estimatedPrice);
                    const {left, right} = chart.chartArea;
                    const context = chart.ctx;
                    context.save();
                    context.beginPath();
                    context.moveTo(left, y);
                    context.lineTo(right, y);
                    context.lineWidth = 2;
                    context.strokeStyle = SALES_HORIZON_COLORS.estimate;
                    context.setLineDash([7, 5]);
                    context.stroke();
                    context.restore();
                },
            };

            instance = new Chart<'bar', number[], string[]>(element, {
                type: 'bar',
                data: {
                    // A label of more lines: the name of the sale mode and its days
                    labels: data.saleModes.map(saleMode => [saleMode.name, formatDays(saleMode)]),
                    datasets: [{
                        data: data.saleModes.map(saleMode => saleMode.price),
                        backgroundColor: SALES_HORIZON_COLORS.column,
                        hoverBackgroundColor: SALES_HORIZON_COLORS.column,
                        borderRadius: 4,
                        maxBarThickness: 90,
                        categoryPercentage: 0.6,
                    }],
                },
                // the line first, the labels over it
                plugins: [estimateLine, columnLabels],
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: {duration: 600},
                    layout: {padding: {top: 36}},
                    font: {family: font},
                    plugins: {
                        legend: {display: false},
                        tooltip: {
                            backgroundColor: 'rgba(23, 28, 40, 0.92)',
                            padding: 12,
                            cornerRadius: 8,
                            displayColors: false,
                            titleFont: {family: font, weight: '600', size: 12},
                            bodyFont: {family: font, size: 13},
                            callbacks: {
                                title: items => data.saleModes[items[0].dataIndex].name,
                                label: item => {
                                    const saleMode = data.saleModes[item.dataIndex];

                                    return [
                                        `${formatMoney(saleMode.price)} (${differenceFromEstimate(saleMode.price, data.estimatedPrice)} proti odhadu)`,
                                        formatPerMeter(saleMode.pricePerMeter),
                                        `Pravděpodobnost ${formatProbability(saleMode.probability)}`,
                                        `Cena vychází z ${formatSales(saleMode.numberOfEstates, data.offerType)}`,
                                    ];
                                },
                            },
                        },
                    },
                    scales: {
                        x: {
                            grid: {display: false, drawBorder: false},
                            ticks: {color: SALES_HORIZON_COLORS.muted, font: {family: font, size: 12}},
                        },
                        y: {
                            min: bounds.min,
                            max: bounds.max,
                            grid: {color: SALES_HORIZON_COLORS.grid, drawBorder: false},
                            ticks: {color: SALES_HORIZON_COLORS.muted, stepSize: bounds.step, callback: value => formatAxisPrice(Number(value)), font: {family: font, size: 12}},
                        },
                    },
                },
            });
        });

        onBeforeUnmount(() => {
            instance?.destroy();
            instance = null;
        });

        return {canvas, summary};
    },
});
</script>
